import {
  EntityType,
  FilterKey,
  FilterOption,
  isValidEntityType,
} from "../types";
import {
  DATA_PAGE_PARAMETER,
  DUPLICATE_THRESHOLD,
  ENTITY_TYPE_PARAMETER,
  ISSUE_TYPE_PARAMETER,
  LABELS_PARAMETER,
  METADATA_SOURCES_PARAMETER,
  PAGE_PARAMETER,
  SEARCH_TEXT_PARAMETER,
  SEARCH_TEXT_THRESHOLD_PARAMETER,
  VQL_PARAMETER,
} from "../helpers/constants/miscellaneous";
import { useDispatch, useSelector } from "react-redux";
import { getEntityTypeFilter } from "../redux/SingleDataset/selectors";
import { useSearchOptions } from "./useFeatureFlagHooks";
import useAllQueryParamsFilters from "./useAllQueryParamsFilters";
import { FILTER_MENU_OPTIONS } from "../helpers/utility/queryFilterHelpers";
import {
  getFilterKeyByFilterType,
  removeFiltersFromVqlQuery,
} from "../helpers/utility/vqlHelpers";
import { useNavigate, useSearchParams } from "react-router-dom";
import { amplitudeTrack } from "../helpers/utility/amplitude";
import { AMP_DATASET_EXPLORATION_EVENT__ENTITY_TYPE_CHANGED } from "../helpers/constants/amplitudeEvents";
import { AMP_DATASET_EXPLORATION_PROPERTY__ENTITY_TYPE } from "../helpers/constants/amplitudeProperties";
import {
  fetchDataset,
  fetchDatasetContextualLabels,
  fetchMetadataSources,
  setCustomMetadata,
} from "../redux/SingleDataset/actions";

export const useEntityTypeFilter = ({
  datasetId,
  onSelect,
}: {
  datasetId: string;
  onSelect: (value: string) => void;
}) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const queryParamsFilters = useAllQueryParamsFilters();
  const [searchParams] = useSearchParams();
  const entityTypeFromQueryParam = isValidEntityType(
    queryParamsFilters[ENTITY_TYPE_PARAMETER],
  )
    ? queryParamsFilters[ENTITY_TYPE_PARAMETER]
    : null;
  const entityTypeFilter = useSelector((state) =>
    getEntityTypeFilter(state, entityTypeFromQueryParam),
  );

  const imageSearchEnabled = useSearchOptions("IMAGES").enabled;
  const objectSearchEnabled = useSearchOptions("OBJECTS").enabled;

  const selectedEntityTypes = entityTypeFilter.filter(
    (option: FilterOption) => option.selected,
  );

  if (!selectedEntityTypes.length) {
    selectedEntityTypes.push(
      entityTypeFilter.find(
        (entity) => entity.text.toLowerCase() === "images",
      )!,
    );
  }

  const resetFilters = (entityType: EntityType) => {
    const filterKeysToRemove = FILTER_MENU_OPTIONS.filter(
      (filter) => !filter.entityTypes.includes(entityType),
    )
      .map((filter) => getFilterKeyByFilterType(filter.type))
      .filter((key): key is FilterKey => !!key);

    if (filterKeysToRemove.length && searchParams.has(VQL_PARAMETER)) {
      removeFiltersFromVqlQuery(searchParams, filterKeysToRemove);
    }
  };
  const handleFilterSelection = (value: string) => {
    const selectedEntityType = entityTypeFilter.find(
      (option: FilterOption) => option.id === value,
    )!;
    if (selectedEntityType?.selected) {
      onSelect(value);
      return;
    }
    onSelect(value);
    resetFilters(selectedEntityType.text.toUpperCase() as EntityType);

    if (searchParams.has(LABELS_PARAMETER)) {
      //remove labels on entity type change
      searchParams.delete(LABELS_PARAMETER);
    }

    if (searchParams.has(SEARCH_TEXT_PARAMETER)) {
      const isImage = selectedEntityType.text.toUpperCase() === "IMAGES";
      const isSearchEnabled = isImage
        ? imageSearchEnabled
        : objectSearchEnabled;

      if (!isSearchEnabled) {
        //remove search query if search is not enabled for the selected entity type
        searchParams.delete(SEARCH_TEXT_PARAMETER);
        searchParams.delete(SEARCH_TEXT_THRESHOLD_PARAMETER);
      }
    }

    amplitudeTrack(AMP_DATASET_EXPLORATION_EVENT__ENTITY_TYPE_CHANGED, {
      [AMP_DATASET_EXPLORATION_PROPERTY__ENTITY_TYPE]: selectedEntityType.text,
    });

    //reset duplicates threshold
    if (searchParams.has(DUPLICATE_THRESHOLD)) {
      searchParams.delete(DUPLICATE_THRESHOLD);
      searchParams.delete(ISSUE_TYPE_PARAMETER);
    }
    searchParams.delete(METADATA_SOURCES_PARAMETER);
    searchParams.delete(DATA_PAGE_PARAMETER);
    searchParams.set(ENTITY_TYPE_PARAMETER, selectedEntityType.text);
    searchParams.set(PAGE_PARAMETER, "1");
    navigate(`/dataset/${datasetId}/data?${searchParams.toString()}`);
    dispatch(
      fetchDatasetContextualLabels(datasetId, {
        [ENTITY_TYPE_PARAMETER]: selectedEntityType.text,
      }) as any,
    );
    dispatch(fetchDataset(datasetId) as any);
    dispatch(
      fetchMetadataSources(
        datasetId!,
        selectedEntityType.text.toUpperCase() as EntityType,
      ) as any,
    );
    if (selectedEntityType.text.toUpperCase() === "OBJECTS") {
      //reset custom metadata
      dispatch(setCustomMetadata(null));
    }
  };
  return { handleFilterSelection, entityTypeFilter, selectedEntityTypes };
};
